/**
 * Quote PDF readiness helpers for send workflow lists.
 */

export const getQuotePdfStatus = (quote) => quote?.quotePdf?.status || "missing";

export const getQuoteItemsListPdfStatus = (quote) =>
  quote?.quoteItemsListPdf?.status || "missing";

export const isUnsentPendingQuote = (quote) => quote?.status === "pending";

export const isQuotePdfReady = (quote) => getQuotePdfStatus(quote) === "ready";

export const isQuoteItemsListPdfReady = (quote) =>
  getQuoteItemsListPdfStatus(quote) === "ready";

export const areQuotePdfsReady = (quote) =>
  isQuotePdfReady(quote) && isQuoteItemsListPdfReady(quote);

export const isQuotePdfPreparing = (quote) => {
  const summary = getQuotePdfStatus(quote);
  const itemsList = getQuoteItemsListPdfStatus(quote);
  return summary === "generating" || itemsList === "generating";
};

const GENERATABLE_STATUSES = [
  "missing",
  "deleted",
  "failed",
  "stale",
  "queued",
  "generating",
];

export const canGenerateQuotePdf = (quote) => {
  const summaryStatus = getQuotePdfStatus(quote);
  const itemsListStatus = getQuoteItemsListPdfStatus(quote);
  return (
    GENERATABLE_STATUSES.includes(summaryStatus) ||
    GENERATABLE_STATUSES.includes(itemsListStatus)
  );
};

export const isReadyToSendQuote = (quote) =>
  isUnsentPendingQuote(quote);

/**
 * Automated preparation quotes require explicit Ready-to-Send approval (W15-E02-006).
 * Quotes without automatedPreparation are unchanged (legacy/manual).
 * @param {Object} quote
 * @returns {boolean}
 */
export const isAutomatedPreparationApprovedForSend = (quote) => {
  const prep = quote?.automatedPreparation;
  if (!prep) return true;
  return Boolean(prep.readyToSend);
};

/**
 * Send is allowed without pre-generated PDFs — send force-regenerates them.
 * Excludes send_queued (undo window) so a second send cannot be queued.
 * @param {Object} quote
 * @returns {boolean}
 */
export const canSendQuoteToCustomer = (quote) =>
  isReadyToSendQuote(quote) &&
  quote?.status !== "send_queued" &&
  isAutomatedPreparationApprovedForSend(quote);

/**
 * True when automated prep still needs admin approval.
 * @param {Object} quote
 * @returns {boolean}
 */
export const needsAutomatedPreparationApproval = (quote) =>
  Boolean(quote?.automatedPreparation) &&
  isUnsentPendingQuote(quote) &&
  !isAutomatedPreparationApprovedForSend(quote);

export const isPdfNotReadyQuote = (quote) =>
  isUnsentPendingQuote(quote) && !areQuotePdfsReady(quote);

export const getQuotePdfStatusLabel = (status) => {
  const labels = {
    missing: "Not generated",
    queued: "Out of date",
    generating: "Generating",
    ready: "Ready",
    stale: "Out of date",
    deleted: "Removed",
    failed: "Failed",
  };
  return labels[status] || status;
};

export const getQuotePdfStatusColor = (status) => {
  const colors = {
    missing: "default",
    queued: "info",
    generating: "info",
    ready: "success",
    stale: "warning",
    deleted: "default",
    failed: "error",
  };
  return colors[status] || "default";
};

export const getCombinedPdfStatusColor = (quote) => {
  if (areQuotePdfsReady(quote)) {
    return "success";
  }
  if (isQuotePdfPreparing(quote)) {
    return "info";
  }
  const summary = getQuotePdfStatus(quote);
  const itemsList = getQuoteItemsListPdfStatus(quote);
  if (summary === "failed" || itemsList === "failed") {
    return "error";
  }
  if (summary === "stale" || itemsList === "stale") {
    return "warning";
  }
  if (summary === "queued" || itemsList === "queued") {
    return "info";
  }
  return "default";
};

export const getCombinedPdfStatusLabel = (quote) => {
  const summary = getQuotePdfStatus(quote);
  const itemsList = getQuoteItemsListPdfStatus(quote);

  if (summary === "ready" && itemsList === "ready") {
    return "Ready";
  }
  if (summary === "generating" || itemsList === "generating") {
    return "Generating";
  }
  if (summary === "failed" || itemsList === "failed") {
    return "Failed";
  }
  if (summary === "queued" || itemsList === "queued") {
    return "Queued";
  }
  if (summary === "stale" || itemsList === "stale") {
    return "Out of date";
  }
  return "Not generated";
};
